import React, { FC, FormEventHandler, useEffect, useState } from 'react'
import * as stripeJs from '@stripe/stripe-js'
import { StripePaymentElementOptions } from '@stripe/stripe-js'
import { PaymentElement } from '@stripe/react-stripe-js'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'

// const CARD_OPTIONS: StripeCardElementOptions = {
//   iconStyle: 'solid',
//   hidePostalCode: true,
//   style: {
//     base: {
//       iconColor: '#0c1b50',
//       color: '#0c1b50',
//       fontWeight: 400,
//       fontFamily: 'Gilroy, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto,',
//       fontSize: '16px',
//       fontSmoothing: 'antialiased',
//       ':-webkit-autofill': {
//         color: '#0c1b50',
//       },
//       '::placeholder': {
//         color: '#6c757d',
//       },
//     },
//     invalid: {
//       iconColor: '#0c1b50',
//       color: '#DE5246',
//     },
//   },
// }

type StripeCardElementChangeEventHandler = (event: stripeJs.StripePaymentElementChangeEvent) => any

const SubmitButton: FC<{ isLoading: boolean; error: boolean; disabled: boolean }> = ({
  isLoading,
  error,
  children,
  disabled,
}) => (
  <button
    className={`btn btn-primary btn-lg btn-block ${error ? 'SubmitButton--error' : ''}`}
    type="submit"
    disabled={isLoading || disabled}
  >
    {isLoading ? 'Processing...' : children}
  </button>
)

const ErrorMessage: FC = ({ children }) => (
  <div className="alert alert-danger alert-dismissible fade show" role="alert">
    {children}
    <button type="button" className="close" data-dismiss="alert" aria-label="Close">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>
)

export type BillingDetails = {
  title: string
  name: string
  email: string
  phone: string
}

type Props = {
  onSubmit: (billingDetails: BillingDetails) => void
  isLoading: boolean
  hasPaymentError: boolean
}

const CheckoutForm: FC<Props> = ({ onSubmit, isLoading, hasPaymentError }) => {
  const [error, setError] = useState<null | string>(null)
  const [disabled, setDisabled] = useState(true)
  const [stripeMounted, setStripeMounted] = useState(false)

  const { user } = useSelector((state: RootState) => state.app)

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: {
      type: 'accordion',
      defaultCollapsed: false,
      radios: true,
      spacedAccordionItems: false,
    },
  }

  useEffect(() => {
    if (hasPaymentError) {
      setError('Payment rejected. Contact you bank or try another payment method')
    } else {
      setError('')
    }
  }, [hasPaymentError])

  const [billingDetails, setBillingDetails] = useState<BillingDetails>({
    title: 'MR',
    email: user?.email || '',
    phone: user?.phone_number || '',
    name: `${user?.first_name} ${user?.last_name}`.trim(),
  })

  const handleChange: StripeCardElementChangeEventHandler = async (event) => {
    setDisabled(!event.complete)
  }

  const handleFormSubmission: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()

    onSubmit?.(billingDetails)
  }

  return (
    <form className={`${hasPaymentError ? 'has-error' : ''}`} onSubmit={handleFormSubmission}>
      {!stripeMounted && (
        <div style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="spinner-border text-light" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}
      <div className="mb-2">
        <PaymentElement
          options={paymentElementOptions}
          onChange={handleChange}
          onReady={() => setStripeMounted(true)}
        />
      </div>
      {stripeMounted && (
        <>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <SubmitButton isLoading={isLoading} error={!!error} disabled={isLoading || disabled}>
            {isLoading ? (
              <div className="spinner-border text-light" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            ) : (
              'Complete'
            )}
          </SubmitButton>
        </>
      )}
    </form>
  )
}

export default CheckoutForm
